import { lazy, Suspense } from "react";
import { Switch, Route, Link } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { ProtectedRoute } from "@/lib/protected-route";
import { Loader2 } from "lucide-react";
import { HelmetProvider } from "react-helmet-async";

// Pages. The landing page ("/") stays eagerly imported — lazy-loading it
// would add a round trip before first paint. Everything else is code-split.
import LandingPage from "@/pages/landing";
import NotFound from "@/pages/not-found";

const AuthPage = lazy(() => import("@/pages/auth"));
const Contact = lazy(() => import("@/pages/contact"));
const PrivacyPolicy = lazy(() => import("@/pages/privacy"));
const TermsAndConditions = lazy(() => import("@/pages/terms"));
const ZamploPage = lazy(() => import("@/pages/zamplo"));
const SubmitterDashboard = lazy(() => import("@/pages/submitter/dashboard"));
const SubmitterDealsList = lazy(() => import("@/pages/submitter/deals-list"));
const SubmitterDealDetail = lazy(() => import("@/pages/submitter/deal-detail"));
const DealForm = lazy(() => import("@/pages/submitter/deal-form"));
// Funder pages removed - direct funding model only
const AdminDashboard = lazy(() => import("@/pages/admin/dashboard"));
const AdminContactMessages = lazy(() => import("@/pages/admin/contact-messages"));
const AdminDealsList = lazy(() => import("@/pages/admin/deals-list"));
const AdminDealDetail = lazy(() => import("@/pages/admin/deal-detail"));
const AdminUsersList = lazy(() => import("@/pages/admin/users-list"));
const AdminWebhooks = lazy(() => import("@/pages/admin/webhooks"));
const AdminPromos = lazy(() => import("@/pages/admin/promos"));

// Dashboard Layout wrapper
function DashboardLayout({ children }: { children: React.ReactNode }) {
  const { user, logoutMutation } = useAuth();

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-border" />
      </div>
    );
  }

  const style = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3rem",
  };

  return (
    <SidebarProvider style={style as React.CSSProperties}>
      <div className="flex h-screen w-full">
        <AppSidebar 
          userType={user.userType} 
          userName={user.name} 
          userEmail={user.email}
          onLogout={() => logoutMutation.mutate()}
        />
        <div className="flex flex-col flex-1 overflow-hidden">
          <header className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-border bg-background">
            <div className="flex items-center gap-2 sm:gap-4">
              <SidebarTrigger data-testid="button-sidebar-toggle" />
              {user.userType === "admin" && (
                <Link href="/">
                  <img 
                    src="/img/logo-full.webp" 
                    alt="LandPartner" 
                    width={320}
                    height={221}
                    loading="eager"
                    decoding="async"
                    className="h-10 sm:h-12 md:h-16 w-auto cursor-pointer flex-shrink-0"
                    data-testid="logo-image"
                  />
                </Link>
              )}
            </div>
            {/* Hide navigation on mobile - sidebar has all links */}
            <nav className="hidden lg:flex items-center gap-8">
              <Link href={user.userType === "admin" ? "/admin" : "/dashboard/submitter"}>
                <button className="text-foreground hover:text-primary transition-colors font-medium">
                  Dashboard
                </button>
              </Link>
              <Link href={user.userType === "admin" ? "/admin/deals" : "/dashboard/submitter/deals/new"}>
                <button className="text-foreground hover:text-primary transition-colors font-medium">
                  {user.userType === "admin" ? "Manage Deals" : "Submit Deal"}
                </button>
              </Link>
              <Link href="/#what-you-get">
                <button className="text-foreground hover:text-primary transition-colors font-medium">
                  How It Works
                </button>
              </Link>
              <Link href="/contact">
                <button className="text-foreground hover:text-primary transition-colors font-medium">
                  Contact
                </button>
              </Link>
            </nav>
          </header>
          {/* div, not <main> — the single <main> landmark wraps the router in App() */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-background">
            <div className="max-w-screen-2xl mx-auto">
              {children}
            </div>
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}

function Router() {

  return (
    <Suspense fallback={<div className="min-h-screen" />}>
      <Switch>
      {/* Public Routes */}
      <Route path="/" component={LandingPage} />
      <Route path="/auth" component={AuthPage} />
      <Route path="/contact" component={Contact} />
      <Route path="/privacy" component={PrivacyPolicy} />
      <Route path="/terms" component={TermsAndConditions} />
      <Route path="/zamplo" component={ZamploPage} />

      {/* Submitter Routes */}
      <ProtectedRoute path="/dashboard/submitter" allowedUserTypes={["submitter"]} component={() => (
        <DashboardLayout><SubmitterDashboard /></DashboardLayout>
      )} />
      <ProtectedRoute path="/dashboard/submitter/deals" allowedUserTypes={["submitter"]} component={() => (
        <DashboardLayout><SubmitterDealsList /></DashboardLayout>
      )} />
      <ProtectedRoute path="/dashboard/submitter/deals/new" allowedUserTypes={["submitter"]} component={() => (
        <DashboardLayout><DealForm /></DashboardLayout>
      )} />
      <ProtectedRoute path="/dashboard/submitter/deals/:id" allowedUserTypes={["submitter"]} component={() => (
        <DashboardLayout><SubmitterDealDetail /></DashboardLayout>
      )} />

      {/* Funder Routes - REMOVED (Direct funding model) */}

      {/* Admin Routes */}
      <ProtectedRoute path="/admin" allowedUserTypes={["admin"]} component={() => (
        <DashboardLayout><AdminDashboard /></DashboardLayout>
      )} />
      <ProtectedRoute path="/admin/users" allowedUserTypes={["admin"]} component={() => (
        <DashboardLayout><AdminUsersList /></DashboardLayout>
      )} />
      <ProtectedRoute path="/admin/deals" allowedUserTypes={["admin"]} component={() => (
        <DashboardLayout><AdminDealsList /></DashboardLayout>
      )} />
      <ProtectedRoute path="/admin/deals/:id" allowedUserTypes={["admin"]} component={() => (
        <DashboardLayout><AdminDealDetail /></DashboardLayout>
      )} />
      <ProtectedRoute path="/admin/contact-messages" allowedUserTypes={["admin"]} component={() => (
        <DashboardLayout><AdminContactMessages /></DashboardLayout>
      )} />
      <ProtectedRoute path="/admin/webhooks" allowedUserTypes={["admin"]} component={() => (
        <DashboardLayout><AdminWebhooks /></DashboardLayout>
      )} />
      <ProtectedRoute path="/admin/promos" allowedUserTypes={["admin"]} component={() => (
        <DashboardLayout><AdminPromos /></DashboardLayout>
      )} />

      {/* Fallback to 404 */}
      <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <a
              href="#main-content"
              className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:rounded focus:bg-background focus:px-4 focus:py-2 focus:shadow"
            >
              Skip to main content
            </a>
            <main id="main-content">
              <Router />
            </main>
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}
